import { createAuthedAxiosClient } from '../../api-client/api-client';

/**
 * Site-admin partner API key wrapper.
 *
 * These endpoints are not in the generated client yet, so this calls them directly through the
 * shared authenticated axios client (Cognito Bearer + retry + error handling). The types mirror the
 * backend DTOs in tsoa-src/partner-api-keys. The raw key is only returned by `revealPartnerApiKey`.
 */

export interface IPartnerApiKeyView {
  id: string;
  name: string;
  description: string | null;
  subscriberId: string;
  subscriberName: string | null;
  keyPrefix: string;
  isSandbox: boolean;
  disabled: boolean;
  rateLimitPerMinute: number;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdOn: string | null;
  updatedOn: string | null;
}

export interface ICreatePartnerApiKeyRequest {
  subscriberId: string;
  name: string;
  description?: string | null;
  isSandbox?: boolean;
  rateLimitPerMinute?: number;
  expiresAt?: string | null;
}

export interface IEditPartnerApiKeyRequest {
  name?: string;
  description?: string | null;
  disabled?: boolean;
  rateLimitPerMinute?: number;
  expiresAt?: string | null;
}

export interface ICreatePartnerApiKeyResponse extends IPartnerApiKeyView {
  secretName: string;
}

export interface IRevealPartnerApiKeyResponse {
  keyId: string;
  apiKey: string;
}

export interface IPartnerApiKeyEndpointCallCount {
  method: string;
  /** Root resource segment (e.g. "documents", "files", "projects"), not the full path. */
  path: string;
  count: number;
}

export interface IPartnerApiKeyIpCallCount {
  ip: string;
  count: number;
  location: string | null;
}

export interface IPartnerApiKeyStatsResponse {
  keyId: string;
  totalCalls: number;
  uniqueEndpointCount: number;
  uniqueIpCount: number;
  callsByEndpoint: IPartnerApiKeyEndpointCallCount[];
  callsByIp: IPartnerApiKeyIpCallCount[];
}

interface IPaginatedResult<T> {
  results: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const PATH = '/partner-api-keys';

export const listPartnerApiKeys = async (pageSize = 200): Promise<IPartnerApiKeyView[]> => {
  const res = await createAuthedAxiosClient().get<IPaginatedResult<IPartnerApiKeyView>>(PATH, {
    params: { pageSize },
  });
  return res.data.results;
};

export const getPartnerApiKey = async (keyId: string): Promise<IPartnerApiKeyView> => {
  const res = await createAuthedAxiosClient().get<IPartnerApiKeyView>(`${PATH}/${keyId}`);
  return res.data;
};

export const createPartnerApiKey = async (
  body: ICreatePartnerApiKeyRequest,
): Promise<ICreatePartnerApiKeyResponse> => {
  const res = await createAuthedAxiosClient().post<ICreatePartnerApiKeyResponse>(PATH, body);
  return res.data;
};

export const editPartnerApiKey = async (
  keyId: string,
  body: IEditPartnerApiKeyRequest,
): Promise<IPartnerApiKeyView> => {
  const res = await createAuthedAxiosClient().patch<IPartnerApiKeyView>(`${PATH}/${keyId}`, body);
  return res.data;
};

export const revealPartnerApiKey = async (
  keyId: string,
): Promise<IRevealPartnerApiKeyResponse> => {
  const res = await createAuthedAxiosClient().post<IRevealPartnerApiKeyResponse>(
    `${PATH}/${keyId}/reveal`,
  );
  return res.data;
};

export const getPartnerApiKeyStats = async (
  keyId: string,
): Promise<IPartnerApiKeyStatsResponse> => {
  const res = await createAuthedAxiosClient().get<IPartnerApiKeyStatsResponse>(
    `${PATH}/${keyId}/stats`,
  );
  return res.data;
};
